import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box, Radio, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Typography, useMediaQuery,
} from '@mui/material';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { SettingsMobileNavbar } from '../../../root-components/navigation/settings-mobile-navbar';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import gaiaTheme from '../../../root-components/theme';
import OrganisationService from '../../../shared/services/organisation/organisation.service';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model';
import {
  EmissionFieldConfig, EmissionFieldVisibility,
  VehicleFieldConfig,
} from '../../../shared/services/organisation/organisation.model';

// ── Field definitions ─────────────────────────────────────────────────────────

const EMISSION_STANDARD_FIELDS: { key: keyof EmissionFieldConfig; label: string }[] = [
  { key: 'country', label: 'Country' },
  { key: 'location', label: 'Location' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'product', label: 'Product / Capital Good' },
  { key: 'project', label: 'Project' },
  { key: 'ets', label: 'Regulated Emission Trading Scheme' },
  { key: 'excludeFromReporting', label: 'Exclude from Reporting' },
];

const VEHICLE_STANDARD_FIELDS: { key: keyof VehicleFieldConfig; label: string }[] = [
  { key: 'vehicleReference', label: 'Vehicle Reference' },
  { key: 'assignedUserId', label: 'Default Assignee' },
];

const VISIBILITY_OPTIONS: EmissionFieldVisibility[] = ['mandatory', 'optional', 'hidden'];

// ── Page component ────────────────────────────────────────────────────────────

export const SettingsInputFieldsPage = () => {
  const { userDetails, forceUpdateUserDetails } = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const organisationService = new OrganisationService();
  const customFieldService = new CustomFieldService();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState<'emissions' | 'vehicles'>('emissions');
  const [isLoading, setIsLoading] = useState(true);

  // Emission state
  const [emissionFieldConfig, setEmissionFieldConfig] = useState<EmissionFieldConfig>(
    userDetails?.organisation?.emissionFieldConfig ?? {},
  );
  const [emissionCustomFields, setEmissionCustomFields] = useState<ICustomField[]>([]);

  // Vehicle state
  const [vehicleFieldConfig, setVehicleFieldConfig] = useState<VehicleFieldConfig>(
    userDetails?.organisation?.vehicleFieldConfig ?? {},
  );
  const [vehicleCustomFields, setVehicleCustomFields] = useState<ICustomField[]>([]);

  useEffect(() => {
    Promise.all([
      customFieldService.getAllActive('&entryType=transaction'),
      customFieldService.getAllActive('&entryType=vehicle'),
    ]).then(([emissions, vehicles]) => {
      setEmissionCustomFields(emissions);
      setVehicleCustomFields(vehicles);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, []);

  // Keep local configs in sync if context reloads
  useEffect(() => {
    setEmissionFieldConfig(userDetails?.organisation?.emissionFieldConfig ?? {});
  }, [userDetails?.organisation?.emissionFieldConfig]);

  useEffect(() => {
    setVehicleFieldConfig(userDetails?.organisation?.vehicleFieldConfig ?? {});
  }, [userDetails?.organisation?.vehicleFieldConfig]);

  // ── Save helpers ─────────────────────────────────────────────────────────────

  const patchOrg = useCallback(async (patch: Record<string, unknown>) => {
    await organisationService.editOrganisations({
      name: userDetails?.organisation?.name ?? '',
      currencyCode: userDetails?.organisation?.currencyCode ?? 'GBP',
      ...patch,
    } as any);
    await forceUpdateUserDetails();
  }, [userDetails, forceUpdateUserDetails]);

  const handleEmissionFieldChange = useCallback(async (
    key: keyof EmissionFieldConfig, value: EmissionFieldVisibility,
  ) => {
    const newConfig: EmissionFieldConfig = { ...emissionFieldConfig, [key]: value };
    setEmissionFieldConfig(newConfig);
    try {
      await patchOrg({ emissionFieldConfig: newConfig });
      userNotificationsContext.openSnackbar({ open: true, message: 'Field settings saved', backgroundColor: 'primary.dark' });
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to save settings', backgroundColor: 'text.primary' });
    }
  }, [emissionFieldConfig, patchOrg]);

  const handleVehicleFieldChange = useCallback(async (
    key: keyof VehicleFieldConfig, value: EmissionFieldVisibility,
  ) => {
    const newConfig: VehicleFieldConfig = { ...vehicleFieldConfig, [key]: value };
    setVehicleFieldConfig(newConfig);
    try {
      await patchOrg({ vehicleFieldConfig: newConfig });
      userNotificationsContext.openSnackbar({ open: true, message: 'Field settings saved', backgroundColor: 'primary.dark' });
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to save settings', backgroundColor: 'text.primary' });
    }
  }, [vehicleFieldConfig, patchOrg]);

  const handleCustomFieldChange = useCallback(async (
    field: ICustomField, displayMode: 'mandatory' | 'optional' | 'hidden',
    setter: React.Dispatch<React.SetStateAction<ICustomField[]>>,
  ) => {
    setter(prev => prev.map(f => f.id === field.id ? { ...f, displayMode } : f));
    try {
      await customFieldService.updateDisplayMode(field.id, displayMode);
      userNotificationsContext.openSnackbar({ open: true, message: 'Field settings saved', backgroundColor: 'primary.dark' });
    } catch {
      setter(prev => prev.map(f => f.id === field.id ? { ...f, displayMode: field.displayMode } : f));
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to save settings', backgroundColor: 'text.primary' });
    }
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}><SettingsMobileNavbar /></Box>}
      <PageHeading title='Input Fields' hasTabs={false} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label='Emissions' value='emissions' />
          <Tab label='Vehicles' value='vehicles' />
        </Tabs>
      </Box>

      <LoadingPage pageReady={!isLoading}>
        {activeTab === 'emissions' && (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              Control which optional fields appear in the emission entry forms.{' '}
              <strong>Mandatory</strong> fields are shown in the main form and must be filled in.{' '}
              <strong>Optional</strong> fields appear in the collapsible Advanced section.{' '}
              <strong>Hidden</strong> fields are not shown at all.
            </Typography>

            <FieldTable
              title='Standard Fields'
              rows={EMISSION_STANDARD_FIELDS.map(({ key, label }) => ({
                id: key,
                label,
                current: emissionFieldConfig[key] ?? 'optional',
                onChange: (v) => handleEmissionFieldChange(key, v as EmissionFieldVisibility),
              }))}
            />

            {emissionCustomFields.length > 0 && (
              <FieldTable
                title='Custom Fields'
                rows={emissionCustomFields.map(field => ({
                  id: field.id,
                  label: field.fieldName,
                  current: field.displayMode ?? 'optional',
                  onChange: (v) => handleCustomFieldChange(
                    field, v as 'mandatory' | 'optional' | 'hidden', setEmissionCustomFields,
                  ),
                }))}
              />
            )}
          </>
        )}

        {activeTab === 'vehicles' && (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              Control which optional fields appear in the vehicle add and edit forms.{' '}
              <strong>Mandatory</strong> fields must be filled in.{' '}
              <strong>Optional</strong> fields are shown but not required.{' '}
              <strong>Hidden</strong> fields are not shown at all.
            </Typography>

            <FieldTable
              title='Standard Fields'
              rows={VEHICLE_STANDARD_FIELDS.map(({ key, label }) => ({
                id: key,
                label,
                current: vehicleFieldConfig[key] ?? 'optional',
                onChange: (v) => handleVehicleFieldChange(key, v as EmissionFieldVisibility),
              }))}
            />

            {vehicleCustomFields.length > 0 && (
              <FieldTable
                title='Custom Fields'
                rows={vehicleCustomFields.map(field => ({
                  id: field.id,
                  label: field.fieldName,
                  current: field.displayMode ?? 'optional',
                  onChange: (v) => handleCustomFieldChange(
                    field, v as 'mandatory' | 'optional' | 'hidden', setVehicleCustomFields,
                  ),
                }))}
              />
            )}
          </>
        )}
      </LoadingPage>
    </PageOuterContainer>
  );
};

// ── Shared table component ────────────────────────────────────────────────────

interface FieldRow {
  id: string;
  label: string;
  current: string;
  onChange: (value: string) => void;
}

function FieldTable({ title, rows }: { title: string; rows: FieldRow[] }) {
  return (
    <>
      <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1, mt: title === 'Custom Fields' ? 3 : 0 }}>
        {title}
      </Typography>
      <TableContainer sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'lightGrey.light' }}>
              <TableCell sx={{ fontWeight: 600, width: '50%' }}>Field</TableCell>
              {VISIBILITY_OPTIONS.map(opt => (
                <TableCell key={opt} align='center' sx={{ fontWeight: 600, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                  {opt === 'optional' ? 'Optional' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.label}</TableCell>
                {VISIBILITY_OPTIONS.map(opt => (
                  <TableCell key={opt} align='center' padding='checkbox'>
                    <Radio
                      size='small'
                      checked={row.current === opt}
                      onChange={() => row.onChange(opt)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
